import React, { useContext, useState, useEffect } from 'react';
import styled from 'styled-components';
import { ThemeContext } from '../contexts/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../services/authFetch';
import AdminSidebar from '../components/AdminSidebar';

const AdminHomePage = ({ isSidebarOpen, onSidebarToggle }) => {
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: false,
    masterDataOpen: false
  });
  const [studentCount, setStudentCount] = useState(null);
  const [teacherCount, setTeacherCount] = useState(null);
  const [pendingInterupsiCount, setPendingInterupsiCount] = useState(null);
  const [pendingKtsCount, setPendingKtsCount] = useState(null);
  const [pendingSicknessPermitCount, setPendingSicknessPermitCount] = useState(null);
  const [pendingOvertimeReportCount, setPendingOvertimeReportCount] = useState(null);
  const [pendingKersosReportCount, setPendingKersosReportCount] = useState(null);
  const [pendingPsmReportCount, setPendingPsmReportCount] = useState(null);
  const [pendingCounselingCount, setPendingCounselingCount] = useState(null);
  const [pendingWarningLetterCount, setPendingWarningLetterCount] = useState(null);
  const [recentRequests, setRecentRequests] = useState([]);
  const [suratIjinOpen, setSuratIjinOpen] = useState(false);
  const [laporanOpen, setLaporanOpen] = useState(false);
  const [masterDataOpen, setMasterDataOpen] = useState(false);
  const navigate = useNavigate();
  const { darkMode } = useContext(ThemeContext);
  const [user, setUser] = useState(null);
  const [showChatbot, setShowChatbot] = useState(false);
  // Sidebar state is now controlled by parent component via props
  
  // Pagination state for recent requests
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem('user'));
    if (storedUser) {
      setUser(storedUser);
    }
  }, []);

  useEffect(() => {
    async function fetchStudentCount() {
      try {
        const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');

        const response = await authFetch(`${baseUrl}/api/students/count`);

        const data = await response.json();
        setStudentCount(data.count);
      } catch (err) {
        setStudentCount('N/A');
      }
    }
    fetchStudentCount();
  }, []);

  useEffect(() => {
    async function fetchDashboardStats() {
      try {
        const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');

        const response = await authFetch(`${baseUrl}/api/admin/dashboard/stats`); 

        if (!response.ok) throw new Error('Failed to fetch dashboard stats');
        
        const result = await response.json();
        if (result.success) {
          setTeacherCount(result.data.teacher_count);
        }
      } catch (err) {
        console.error('Error fetching dashboard stats:', err);
        setTeacherCount('N/A');
      }
    }
    fetchDashboardStats();
  }, []);

  useEffect(() => {
    async function fetchPendingCounts() {
      try {
        const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
        const response = await authFetch(`${baseUrl}/api/admin/dashboard/counts`);
        if (!response.ok) throw new Error('Failed to fetch pending counts');
        const result = await response.json();
        if (result.success) {
          setPendingInterupsiCount(result.data.pending_interupsi);
          setPendingKtsCount(result.data.pending_kts);
          setPendingSicknessPermitCount(result.data.pending_sickness_permit);
          setPendingOvertimeReportCount(result.data.pending_overtime_report);
          setPendingKersosReportCount(result.data.pending_kersos_report);
          setPendingPsmReportCount(result.data.pending_psm_report);
          setPendingCounselingCount(result.data.pending_counseling);
          setPendingWarningLetterCount(result.data.pending_warning_letter);
        } else {
          throw new Error(result.message || 'Failed to fetch pending counts');
        }
      } catch (err) {
        console.error('Error fetching pending counts:', err);
        setPendingInterupsiCount('N/A');
        setPendingKtsCount('N/A');
        setPendingSicknessPermitCount('N/A');
        setPendingOvertimeReportCount('N/A');
        setPendingKersosReportCount('N/A');
        setPendingPsmReportCount('N/A');
        setPendingCounselingCount('N/A');
        setPendingWarningLetterCount('N/A');
      }
    }
    fetchPendingCounts();
  }, []);

  useEffect(() => {
    async function fetchRecentRequests() {
      try {
        const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');

        const response = await authFetch(`${baseUrl}/api/admin/dashboard/requests`);

        if (!response.ok) throw new Error('Failed to fetch recent requests');
        
        const result = await response.json();
        if (result.success) {
          setRecentRequests(result.data);
          setCurrentPage(1); // Reset to first page when new data is loaded
        }
      } catch (err) {
        console.error('Error fetching recent requests:', err);
        setRecentRequests([]);
        setCurrentPage(1);
      }
    }
    fetchRecentRequests();
  }, []);

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : '';

  // Pagination calculations
  const totalPages = Math.ceil(recentRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentPageItems = recentRequests.slice(startIndex, endIndex);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl,
    approvalRequests: recentRequests,
    summaryStats: [
      { label: 'Total Siswa', value: studentCount, icon: '👨‍🎓', color: '#3354a5', path: '/admin/manajemen-siswa' },
      { label: 'Total Guru', value: teacherCount, icon: '👨‍🏫', color: '#2ec27e', path: '/admin/manajemen-guru' },
      { label: 'Laporan KTS Menunggu', value: pendingKtsCount, icon: '⚠️', color: '#c01c28', path: '/admin/laporan-kts' },
      { label: 'Ijin Interupsi Menunggu', value: pendingInterupsiCount, icon: '📝', color: '#e67700', path: '/admin/surat-ijin' },
    
      { label: 'Ijin Tidak Masuk Menunggu', value: pendingSicknessPermitCount, icon: '🤒', color: '#f59f00', path: '/admin/surat-ijin-tidakmasuk' },
      { label: 'Laporan Lembur Menunggu', value: pendingOvertimeReportCount, icon: '⏰', color: '#5c7cfa', path: '/admin/laporan-lembur' },
      { label: 'Laporan Kersos Menunggu', value: pendingKersosReportCount, icon: '🙏', color: '#37b24d', path: '/admin/laporan-kersos' },
      { label: 'Laporan PSM Menunggu', value: pendingPsmReportCount, icon: '🧘', color: '#845ef7', path: '/admin/laporan-psm' },
      { label: 'Laporan Konseling Menunggu', value: pendingCounselingCount, icon: '🤝', color: '#22b8cf', path: '/admin/konseling' },
      { label: 'SP Menunggu', value: pendingWarningLetterCount, icon: '📜', color: '#e03131', path: '/admin/surat-peringatan' },
    ]
  };

  // Handle approval action
  const handleApproval = (requestId, action) => {
    
    // Here you would update the status of the request in your backend
  };

  return (
    <PageContainer>
      <PageHeader>
        <HeaderContent>
          <h1>Dashboard Admin</h1>
        </HeaderContent>
      </PageHeader>

      <DashboardGrid>
        <MainSection>
          <SummaryCards>
            {adminData.summaryStats.map((stat, index) => (
              <SummaryCard 
                key={index}
                onClick={stat.path ? () => navigate(stat.path) : null}
                style={{ cursor: stat.path ? 'pointer' : 'default' }}
              >
                <SummaryIcon style={{ backgroundColor: darkMode ? '#333' : `${stat.color}20` }}>
                  <span style={{ color: stat.color }}>{stat.icon}</span>
                </SummaryIcon>
                <SummaryContent>
                  <SummaryValue>{stat.value}</SummaryValue>
                  <SummaryLabel>{stat.label}</SummaryLabel>
                </SummaryContent>
              </SummaryCard>
            ))}
          </SummaryCards>

          <SectionCard>
            <SectionHeader>
              <h2>Permohonan Hari Ini</h2>
              {/* <ViewAllButton>Lihat Semua</ViewAllButton> */}
            </SectionHeader>

            <ApprovalList>
              {recentRequests.length > 0 ? (
                currentPageItems.map((request) => (
                  <ApprovalItem key={request.id} status={request.status}>
                    <ApprovalContent>
                      <RequestType type={request.type}>
                        {request.type === 'permission' ? '📝' :
                          request.type === 'report' ? '📊' :
                            request.type === 'counseling' ? '🤝' : '⚠️'}
                      </RequestType>
                      <RequestInfo>
                        <RequestTitle>{request.title}</RequestTitle>
                        <RequestMeta>
                          <MetaItem>Oleh: {request.requestedBy}</MetaItem>
                          <MetaItem>Siswa: {request.studentName}</MetaItem>
                          <MetaItem>Tanggal: {formatDate(request.date)}</MetaItem>
                        </RequestMeta>
                      </RequestInfo>
                      <RequestStatus status={request.status}>
                        {request.status === 'pending' ? 'Menunggu Persetujuan' :
                          request.status === 'approved' ? 'Disetujui' :
                            request.status === 'rejected' ? 'Ditolak' : request.status}
                      </RequestStatus>
                    </ApprovalContent>
                  </ApprovalItem>
                ))
              ) : (
                <EmptyState>
                  
                  <EmptyStateText>Tidak ada permohonan hari ini.</EmptyStateText>
                </EmptyState>
              )}
            </ApprovalList>
            
            {recentRequests.length > 0 && (
              <PaginationContainer>
                <PaginationInfo>
                  Menampilkan {startIndex + 1}-{Math.min(endIndex, recentRequests.length)} dari {recentRequests.length} permohonan
                </PaginationInfo>
                <PaginationControls>
                  <PaginationButton 
                    onClick={handlePrevPage} 
                    disabled={currentPage === 1}
                  >
                    ‹ Sebelumnya
                  </PaginationButton>
                  
                  {(() => {
                    const maxVisiblePages = 5;
                    let pages = [];
                    
                    if (totalPages <= maxVisiblePages) {
                      // Show all pages if total is less than max
                      pages = Array.from({ length: totalPages }, (_, i) => i + 1);
                    } else {
                      // Show limited pages with current page in the middle
                      if (currentPage <= 3) {
                        pages = [1, 2, 3, 4, 5];
                      } else if (currentPage >= totalPages - 2) {
                        pages = [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
                      } else {
                        pages = [currentPage - 2, currentPage - 1, currentPage, currentPage + 1, currentPage + 2];
                      }
                    }
                    
                    return pages.map(page => (
                      <PaginationButton
                        key={page}
                        onClick={() => handlePageChange(page)}
                        active={currentPage === page}
                      >
                        {page}
                      </PaginationButton>
                    ));
                  })()}
                  
                  <PaginationButton 
                    onClick={handleNextPage} 
                    disabled={currentPage === totalPages}
                  >
                    Selanjutnya ›
                  </PaginationButton>
                </PaginationControls>
              </PaginationContainer>
            )}
          </SectionCard>
        </MainSection>
        <SidebarContainer $isOpen={isSidebarOpen}>
          <SidebarOverlay $isOpen={isSidebarOpen} onClick={() => onSidebarToggle && onSidebarToggle()} />
          <SidebarContent $isOpen={isSidebarOpen}>
            <SidebarHeader>
              <h3>Menu Admin</h3>
              <CloseButton onClick={() => onSidebarToggle && onSidebarToggle()}>&times;</CloseButton>
            </SidebarHeader>
            <AdminSidebar user={adminData} accordionState={accordionState} setAccordionState={setAccordionState} onSidebarToggle={onSidebarToggle} />
          </SidebarContent>
        </SidebarContainer>
      </DashboardGrid>
      <ChatbotButton
        aria-label="Chatbot"
        title="Buka Chatbot"
        onClick={() => setShowChatbot(true)}
      >
        <ChatIcon size={window.innerWidth <= 576 ? 28 : 38} />
      </ChatbotButton>
      {showChatbot && (
        <ChatbotModalOverlay onClick={() => setShowChatbot(false)}>
          <ChatbotModalContent onClick={e => e.stopPropagation()}>
            <ChatbotModalHeader>
              <ChatbotModalTitle>Chatbot Bantuan</ChatbotModalTitle>
              <ChatbotModalClose onClick={() => setShowChatbot(false)}>&times;</ChatbotModalClose>
            </ChatbotModalHeader>
            <ChatbotIframe
              src="https://automation.canvaswebdesign.com/webhook/6ea2ebe9-ae12-4df0-ab4b-ee13e9cecdba/chat"
              title="Chatbot"
              allow="microphone; camera"
            />
          </ChatbotModalContent>
        </ChatbotModalOverlay>
      )}
    </PageContainer>
  );
};

// Helper function to format dates
const formatDate = (dateString) => {
  const options = { year: 'numeric', month: 'short', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('id-ID', options);
};

const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  
  @media (max-width: 768px) {
    padding: 1rem 0.5rem;
  }
`;

const PageHeader = styled.div`
  margin-bottom: 1.5rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
  
  @media (max-width: 768px) {
    h1 {
      font-size: 1.5rem;
    }
  }
`;

// MenuToggle component removed - now handled by shared Navbar component

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const SidebarContainer = styled.div`
  @media (max-width: 1100px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 1000;
    pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  }
  
  @media (min-width: 1101px) {
    order: 1;
  }
`;

const SidebarOverlay = styled.div`
  display: none;
  
  @media (max-width: 1100px) {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    opacity: ${props => props.$isOpen ? 1 : 0};
    transition: opacity 0.3s ease;
    pointer-events: ${props => props.$isOpen ? 'auto' : 'none'};
  }
`;

const SidebarContent = styled.div`
  @media (max-width: 1100px) {
    position: absolute;
    top: 0;
    left: 0;
    width: 280px;
    height: 100vh;
    background-color: var(--background);
    box-shadow: 2px 0 10px rgba(0, 0, 0, 0.1);
    transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
    transition: transform 0.3s ease;
    overflow-y: auto;
    z-index: 1001;
  }
  
  @media (max-width: 576px) {
    width: 260px;
  }
`;

const SidebarHeader = styled.div`
  display: none;
  
  @media (max-width: 1100px) {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem 1.5rem;
    border-bottom: 1px solid var(--border);
    background-color: var(--primary);
    color: white;
    
    h3 {
      margin: 0;
      font-size: 1.125rem;
      font-weight: 600;
    }
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  order: 2;
  
  @media (max-width: 1100px) {
    order: 1;
  }
`;

const SideSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  order: 1;
  
  @media (max-width: 1100px) {
    order: 2;
  }
`;

const SummaryCards = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
  
  @media (max-width: 576px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 0.75rem;
  }
`;

const SummaryCard = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  padding: 1.25rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  box-shadow: var(--box-shadow);
  transition: transform 0.2s, box-shadow 0.2s, background-color 0.3s;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
    gap: 0.75rem;
  }
  
  @media (max-width: 576px) {
    padding: 1rem;
    flex-direction: row;
    text-align: left;
  }
`;

const SummaryIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  
  span {
    transition: color 0.3s;
  }
  
  @media (max-width: 768px) {
    width: 40px;
    height: 40px;
    font-size: 1.5rem;
  }
  
  @media (max-width: 576px) {
    width: 36px;
    height: 36px;
    font-size: 1.25rem;
  }
`;

const SummaryContent = styled.div`
  display: flex;
  flex-direction: column;
`;

const SummaryValue = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-dark);
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
  
  @media (max-width: 576px) {
    font-size: 1.125rem;
  }
`;

const SummaryLabel = styled.div`
  font-size: 0.875rem;
  color: var(--text-light);
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
  }
  
  @media (max-width: 576px) {
    font-size: 0.75rem;
  }
`;

const SectionCard = styled.div`
  background-color: var(--background);
  border-radius: 16px;
  overflow: hidden;
  box-shadow: var(--box-shadow);
  transition: background-color 0.3s;
`;

const SectionHeader = styled.div`
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid var(--border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
  }
`;

const ViewAllButton = styled.button`
  background: transparent;
  border: none;
  color: var(--primary);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.8;
    text-decoration: underline;
  }
`;

const ApprovalList = styled.div`
  padding: 0.75rem;
`;

const ApprovalItem = styled.div`
  border-radius: 12px;
  margin-bottom: 0.75rem;
  background-color: var(--background-light);
  border-left: 4px solid ${props => {
    switch (props.status) {
      case 'pending': return '#e67700'; // orange
      case 'approved': return '#2ec27e'; // green
      case 'rejected': return '#c01c28'; // red
      default: return 'var(--border)';
    }
  }};
  overflow: hidden;
  transition: transform 0.2s, box-shadow 0.2s, background-color 0.3s;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ApprovalContent = styled.div`
  padding: 1rem;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    padding: 0.875rem;
  }
  
  @media (max-width: 576px) {
    padding: 0.75rem;
    gap: 0.5rem;
  }
`;

const RequestType = styled.div`
  font-size: 1.5rem;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background-color: ${props => {
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    if (isDark) {
      switch (props.type) {
        case 'permission': return '#2c3b56'; // dark blue
        case 'report': return '#1e3a32'; // dark green
        case 'counseling': return '#3a3020'; // dark yellow
        case 'discipline': return '#3a2a2a'; // dark red
        default: return '#333333';
      }
    } else {
      switch (props.type) {
        case 'permission': return '#e8f1fd'; // light blue
        case 'report': return '#e6f8f5'; // light green
        case 'counseling': return '#fff3e0'; // light yellow
        case 'discipline': return '#ffe6e6'; // light red
        default: return '#f5f5f5';
      }
    }
  }};
  flex-shrink: 0;
`;

const RequestInfo = styled.div`
  flex: 1;
`;

const RequestTitle = styled.h4`
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: var(--text-dark);
`;

const RequestMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  font-size: 0.813rem;
  color: var(--text-light);
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.25rem;
    font-size: 0.75rem;
  }
  
  @media (max-width: 576px) {
    font-size: 0.6875rem;
  }
`;

const MetaItem = styled.span`
  display: inline-block;
`;

const RequestStatus = styled.div`
  font-size: 0.813rem;
  font-weight: 600;
  padding: 0.375rem 0.75rem;
  border-radius: 50px;
  white-space: nowrap;
  flex-shrink: 0;
  
  background-color: ${props => {
    const isDark = document.documentElement.getAttribute('data-theme') === 'dark';
    if (isDark) {
      switch (props.status) {
        case 'pending': return '#3a3020'; // dark yellow
        case 'approved': return '#1e3a32'; // dark green
        case 'rejected': return '#3a2a2a'; // dark red
        default: return '#333333';
      }
    } else {
      switch (props.status) {
        case 'pending': return '#fff3e0'; // light yellow
        case 'approved': return '#e6f8f5'; // light green
        case 'rejected': return '#ffe6e6'; // light red
        default: return '#f5f5f5';
      }
    }
  }};
  
  color: ${props => {
    switch (props.status) {
      case 'pending': return '#e67700'; // orange
      case 'approved': return '#2ec27e'; // green
      case 'rejected': return '#c01c28'; // red
      default: return 'var(--text-dark)';
    }
  }};
  
  @media (max-width: 768px) {
    align-self: flex-start;
    width: 100%;
    text-align: center;
    margin-top: 0.5rem;
  }
  
  @media (max-width: 576px) {
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  text-align: center;
`;

const EmptyStateIcon = styled.div`
  font-size: 3rem;
  margin-bottom: 1rem;
  opacity: 0.5;
`;

const EmptyStateText = styled.p`
  font-size: 1rem;
  color: var(--text-light);
  margin: 0;
  font-weight: 500;
`;

const ChatbotModalOverlay = styled.div`
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.45);
  z-index: 3000;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const ChatbotModalContent = styled.div`
  background: var(--background, #fff);
  border-radius: 18px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.25);
  width: 420px;
  max-width: 95vw;
  height: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
  @media (max-width: 576px) {
    width: 98vw;
    height: 90vh;
    border-radius: 10px;
  }
`;
const ChatbotModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem 0.5rem 1.25rem;
  background: var(--primary, #3354a5);
  color: #fff;
`;
const ChatbotModalTitle = styled.div`
  font-weight: 600;
  font-size: 1.1rem;
`;
const ChatbotModalClose = styled.button`
  background: none;
  border: none;
  color: #fff;
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0 0.5rem;
  line-height: 1;
`;
const ChatbotIframe = styled.iframe`
  flex: 1;
  border: none;
  width: 100%;
  min-height: 0;
  background: #f8f8f8;
`;
const ChatbotButton = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
  transition: all 0.3s ease;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.6);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    width: 56px;
    height: 56px;
    bottom: 1.5rem;
    right: 1.5rem;
    font-size: 1.375rem;
  }
  
  @media (max-width: 576px) {
    width: 52px;
    height: 52px;
    bottom: 1rem;
    right: 1rem;
    font-size: 1.25rem;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border);
  background-color: var(--background);
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: center;
    padding: 0.875rem;
  }
  
  @media (max-width: 576px) {
    padding: 0.75rem;
    gap: 0.75rem;
  }
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: var(--text-light);
  font-weight: 500;
  
  @media (max-width: 576px) {
    font-size: 0.75rem;
    text-align: center;
  }
`;

const PaginationControls = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  flex-wrap: wrap;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: ${props => props.active ? 'var(--primary)' : 'var(--background)'};
  color: ${props => props.active ? 'white' : 'var(--text-dark)'};
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 40px;
  
  &:hover:not(:disabled) {
    background: ${props => props.active ? 'var(--primary)' : 'var(--background-light)'};
    border-color: var(--primary);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background: var(--background-light);
  }
  
  @media (max-width: 576px) {
    padding: 0.375rem 0.5rem;
    font-size: 0.75rem;
    min-width: 32px;
  }
`;
const ChatIcon = ({ size = 38 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ display: 'block' }}>
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
  </svg>
);

export default AdminHomePage;